import { Grid, styled, Typography } from '@ori-ui/mui';
import type {
  GlobalCustomerRegistrationsRegistrationLegalDetailsDto,
  GlobalCustomersCustomerDocumentDto,
  GlobalCustomersCustomerDocumentsDto,
} from '@ori/global-api-client-react';
import type { StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto } from '@ori/static-api-client-react';
import useTestId from '@ori/testid-generator';
import type { FC, ReactElement } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { Localization } from '../../../Localization';
import { defaultTranslations } from '../../../constants/defaultTranslations_simplified-registration-BrandPartner';
import type { ILocalizationKey } from '../../../interfaces';
import { TranslationKey } from '../../../models';
import { Document } from '../../Document';

const RootContainer = styled('div')(({ theme }) => ({
  position: 'relative',
}));

export interface ILegalDocumentsProps {
  documentsConfig?: StaticCustomerRegistrationsMetadataRegistrationLegalDetailsMetadataDto;
}

export const LegalDocuments: FC<ILegalDocumentsProps> = (props) => {
  const { documentsConfig } = props;
  const [localDocuments, setLocalDocuments] = useState<GlobalCustomersCustomerDocumentsDto>();

  const { getTestIdProps } = useTestId();

  const { setValue, watch, trigger } = useFormContext<GlobalCustomerRegistrationsRegistrationLegalDetailsDto>();

  const values = watch();

  const getDocumentLabel = (localization: ILocalizationKey) => (
    <Typography
      fontWeight="bold"
      variant="body1"
    >
      <Localization {...localization} />
    </Typography>
  );

  const updateIdentityCard = (documentIds: number[]) => {
    const documents: GlobalCustomersCustomerDocumentDto[] = documentIds.map((id) => ({
      documentId: id,
    }));

    setLocalDocuments({ ...documents, documentsCollection: documents });
    setValue('identityCard.documentsCollection', documents as never);
    trigger('identityCard.documentsCollection');
  };

  const updateAdditionalIdentityCard = (documentIds: number[]) => {
    const documents: GlobalCustomersCustomerDocumentDto[] = documentIds.map((id) => ({
      documentId: id,
    }));

    setLocalDocuments({ ...documents, documentsCollection: documents });
    setValue('additionalIdentityCard.documentsCollection', documents as never);
    trigger('additionalIdentityCard.documentsCollection');
  };

  const getDocumentIds = (documents?: GlobalCustomersCustomerDocumentsDto): number[] => {
    if (!documents) {
      return [];
    }
    // eslint-disable-next-line  @typescript-eslint/no-unnecessary-condition -- we need this condition
    return documents.documentsCollection?.map((d) => d.documentId);
  };

  const renderIdentityCardDocument = (): ReactElement | null => {
    const config = documentsConfig?.identityCard;
    const isAvailable = config?.isAvailable ?? false;
    const isEditable = config?.isEditable ?? false;

    const idCardDocumentInfoText = (
      <Localization
        id={TranslationKey.ThirdStep_IDCard_Text}
        defaultMessage={defaultTranslations.ThirdStep_IDCard_Text}
      />
    );

    if (!isAvailable) {
      return null;
    }

    const documentTypeId = config?.documentTypeId ?? 0;
    const correctDocuments = values.identityCard ?? localDocuments;

    return (
      <Document
        isDeleteConfirmationRequired
        documentTypeId={documentTypeId}
        documentLabel={getDocumentLabel({
          id: TranslationKey.ThirdStep_IDCard_Header,
          defaultMessage: defaultTranslations.ThirdStep_IDCard_Header,
        })}
        documentInfoText={idCardDocumentInfoText}
        documentIds={getDocumentIds(correctDocuments)}
        handleDocumentsChange={updateIdentityCard}
        config={config}
        isRequired={config?.validation?.isMandatory}
        editableDocument={isEditable}
        name="identityCard.documentsCollection"
      />
    );
  };

  const renderAdditionalIdentityCardDocument = (): ReactElement | null => {
    const config = documentsConfig?.additionalIdentityCard;
    const isAvailable = config?.isAvailable ?? false;
    const isEditable = config?.isEditable ?? false;

    const additionalDocumentInfoText = (
      <Localization
        id={TranslationKey.ThirdStep_AdditionalIDCard_Text}
        defaultMessage={defaultTranslations.ThirdStep_AdditionalIDCard_Text}
      />
    );

    if (!isAvailable) {
      return null;
    }

    const documentTypeId = config?.documentTypeId ?? 0;
    const correctDocuments = values.additionalIdentityCard ?? localDocuments;

    return (
      <Document
        isDeleteConfirmationRequired
        documentTypeId={documentTypeId}
        documentLabel={getDocumentLabel({
          id: TranslationKey.ThirdStep_AdditionalIDCard_Header,
          defaultMessage: defaultTranslations.ThirdStep_AdditionalIDCard_Header,
        })}
        documentInfoText={additionalDocumentInfoText}
        documentIds={getDocumentIds(correctDocuments)}
        handleDocumentsChange={updateAdditionalIdentityCard}
        editableDocument={isEditable}
        isRequired={config?.validation?.isMandatory}
        name="additionalIdentityCard.documentsCollection"
      />
    );
  };

  const idCard = renderIdentityCardDocument();
  const additionalIdCard = renderAdditionalIdentityCardDocument();

  const documentsAvailableContent = (
    <Grid
      container
      {...getTestIdProps('documents')}
    >
      {idCard}
      {additionalIdCard}
    </Grid>
  );

  const documentsContent =
    documentsConfig?.identityCard?.isAvailable || documentsConfig?.additionalIdentityCard?.isAvailable
      ? documentsAvailableContent
      : null;

  return <RootContainer>{documentsContent}</RootContainer>;
};
